import { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n";

/**
 * The text field a list row swaps its name for while the user renames it.
 *
 * Enter and leaving the field commit; Escape gives up. An empty or blank value is
 * never committed: the row keeps the name it had, because a nameless area renders as
 * a blank, unlabelled row in the list, on the map and in the plan-hunt area picker.
 */
export function InlineNameEditor({
  initialName,
  onCommit,
  onCancel,
}: {
  initialName: string;
  /** Called with the trimmed name, and only when it is non-empty and changed. */
  onCommit: (name: string) => void;
  /** Called whenever editing ends without a commit. */
  onCancel: () => void;
}) {
  const { t } = useTranslation("map");
  const [value, setValue] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  // Enter commits and then unmounts the field, which fires blur on the way out; without
  // this the blur would commit (or cancel) a second time.
  const settled = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const finish = (commit: boolean) => {
    if (settled.current) return;
    settled.current = true;
    const name = value.trim();
    if (commit && name && name !== initialName) onCommit(name);
    else onCancel();
  };

  return (
    <input
      ref={inputRef}
      className="wh-input"
      aria-label={t("area.nameLabel")}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          finish(true);
        } else if (e.key === "Escape") {
          // The editor closes its own panels on Escape; renaming a row must not.
          e.stopPropagation();
          finish(false);
        }
      }}
      onBlur={() => finish(true)}
      style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, padding: "6px 8px" }}
    />
  );
}
